import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Building2, Briefcase, FileText, Users, LogOut, Plus, Loader2,
  Upload, Edit3, Trash2, CheckCircle, Clock, X, AlertCircle,
  ChevronDown, ChevronUp, Eye, MapPin, CreditCard, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending: { label: 'En attente de validation', color: 'bg-amber-50 text-amber-600', icon: Clock },
  approved: { label: 'Publiée', color: 'bg-green-50 text-green-600', icon: CheckCircle },
  rejected: { label: 'Refusée', color: 'bg-red-50 text-red-600', icon: X },
};

const EmployeurDashboard = () => {
  const { user, token, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('offres');
  const [profile, setProfile] = useState(null);
  const [offers, setOffers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [postingStatus, setPostingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);
  const [expandedOffer, setExpandedOffer] = useState(null);
  const fileRef = useRef(null);

  const [profileForm, setProfileForm] = useState({
    company_name: '', sector: '', description: '', website: '', address: '', contact_name: '', contact_phone: ''
  });

  const emptyOffer = { title: '', department: '', location: '', contract_type: 'CDI', salary: '', start_date: '', deadline: '', summary: '', missions: [''], profile: [''], conditions: [''], tags: [''] };
  const [offerForm, setOfferForm] = useState(emptyOffer);
  const [uploadForm, setUploadForm] = useState({ title: '', location: '', contract_type: 'CDI', file: null });

  const [subscribing, setSubscribing] = useState(false);
  const [subscription, setSubscription] = useState(null);

  const handleSubscribe = async (plan) => {
    setSubscribing(true);
    try {
      const { data } = await axios.post(`${API}/espace-emploi/employeur/subscribe`, {
        plan,
        origin_url: window.location.origin
      }, { headers });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
      setSubscribing(false);
    }
  };

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'employeur')) navigate('/espace-emploi');
  }, [user, authLoading, navigate]);

  useEffect(() => { if (token) fetchAll(); }, [token]);

  // Handle Stripe return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const payment = params.get('payment');
    if (payment === 'success' && sessionId && token) {
      axios.get(`${API}/espace-emploi/employeur/subscription/verify/${sessionId}`, { headers })
        .then(res => {
          if (res.data.active) {
            toast.success('Abonnement activé ! Offres illimitées.');
            fetchAll();
          }
        })
        .catch(() => toast.error('Erreur de vérification du paiement'))
        .finally(() => {
          window.history.replaceState({}, '', window.location.pathname);
        });
    } else if (payment === 'cancelled') {
      toast.info('Paiement annulé');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [token]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [profRes, offRes, appsRes, subRes] = await Promise.all([
        axios.get(`${API}/espace-emploi/profile`, { headers }),
        axios.get(`${API}/espace-emploi/employeur/offers`, { headers }),
        axios.get(`${API}/espace-emploi/employeur/applications`, { headers }),
        axios.get(`${API}/espace-emploi/employeur/subscription`, { headers }),
      ]);
      const p = profRes.data.profile || {};
      setProfile(p);
      setProfileForm(prev => ({ ...prev, ...p }));
      setPostingStatus(profRes.data.posting_status);
      setOffers(offRes.data);
      setApplications(appsRes.data);
      setSubscription(subRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/espace-emploi/employeur/profile`, profileForm, { headers });
      toast.success('Profil entreprise mis à jour');
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const submitOffer = async () => {
    setSaving(true);
    try {
      const clean = {
        ...offerForm,
        missions: offerForm.missions.filter(m => m.trim()),
        profile: offerForm.profile.filter(p => p.trim()),
        conditions: offerForm.conditions.filter(c => c.trim()),
        tags: offerForm.tags.filter(t => t.trim()),
      };
      await axios.post(`${API}/espace-emploi/employeur/offers`, clean, { headers });
      toast.success('Offre soumise ! Elle sera publiée après validation par AERH.');
      setShowForm(false);
      setOfferForm(emptyOffer);
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const submitUpload = async () => {
    if (!uploadForm.file) { toast.error('Selectionnez un fichier'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', uploadForm.title);
      fd.append('location', uploadForm.location);
      fd.append('contract_type', uploadForm.contract_type);
      fd.append('file', uploadForm.file);
      await axios.post(`${API}/espace-emploi/employeur/offers/upload`, fd, { headers });
      toast.success('Offre téléchargée ! Validation AERH en cours.');
      setUploadMode(false);
      setUploadForm({ title: '', location: '', contract_type: 'CDI', file: null });
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const deleteOffer = async (id) => {
    if (!confirm('Supprimer cette offre ?')) return;
    try {
      await axios.delete(`${API}/espace-emploi/employeur/offers/${id}`, { headers });
      toast.success('Offre supprimée');
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const updateListField = (setter, field, index, value) => {
    setter(prev => {
      const arr = [...(prev[field] || [])];
      arr[index] = value;
      return { ...prev, [field]: arr };
    });
  };

  const addListItem = (setter, field) => setter(prev => ({ ...prev, [field]: [...(prev[field] || []), ''] }));
  const removeListItem = (setter, field, index) => setter(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));

  if (authLoading || loading) return (
    <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F59E0B]" /></div>
  );

  const canPost = postingStatus?.can_post;

  const TABS = [
    { id: 'offres', label: 'Mes offres', icon: Briefcase },
    { id: 'candidatures', label: 'Candidatures', icon: Users },
    { id: 'profil', label: 'Mon entreprise', icon: Building2 },
    { id: 'abonnement', label: 'Abonnement', icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="employeur-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#92400E] to-[#F59E0B] py-6">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div>
            <p className="text-white/60 text-sm">Espace Employeur</p>
            <h1 className="text-xl font-bold text-white">{profile?.company_name || user?.company || user?.name}</h1>
          </div>
          <Button variant="ghost" onClick={() => { logout(); navigate('/espace-emploi'); }} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 flex gap-0 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id ? 'border-[#F59E0B] text-[#F59E0B]' : 'border-transparent text-gray-400 hover:text-gray-600'
              }`} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
              {t.id === 'candidatures' && applications.length > 0 && (
                <span className="text-[10px] bg-[#F59E0B] text-white px-1.5 py-0.5 rounded-full">{applications.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* ── TAB: OFFRES ── */}
        {tab === 'offres' && (
          <div data-testid="tab-content-offres">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-[#1F2937]">Mes offres d'emploi</h2>
                {postingStatus && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {postingStatus.reason === 'free' ? `${postingStatus.remaining} offre(s) gratuite(s) restante(s)` :
                     postingStatus.reason === 'subscription' ? `Abonnement actif — offres illimitées` :
                     'Limite atteinte'}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => { setShowForm(true); setUploadMode(false); }} disabled={!canPost}
                  className="bg-[#F59E0B] hover:bg-[#D97706] text-white text-sm" data-testid="new-offer-btn">
                  <Plus className="w-4 h-4 mr-1" /> Formulaire
                </Button>
                <Button onClick={() => { setUploadMode(true); setShowForm(false); }} disabled={!canPost}
                  variant="outline" className="border-[#F59E0B] text-[#F59E0B] text-sm" data-testid="upload-offer-btn">
                  <Upload className="w-4 h-4 mr-1" /> PDF/Word
                </Button>
              </div>
            </div>

            {!canPost && postingStatus?.reason === 'limit_reached' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="limit-banner">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Limite d'offres gratuites atteinte</p>
                  <p className="text-xs text-amber-600 mt-1">Souscrivez à un abonnement pour publier en illimité (dès 500 F/mois).</p>
                  <Button onClick={() => setTab('abonnement')} size="sm" className="mt-2 bg-[#F59E0B] hover:bg-[#D97706] text-white text-xs">
                    Voir les abonnements
                  </Button>
                </div>
              </div>
            )}

            {/* Upload mode */}
            {uploadMode && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6" data-testid="upload-form">
                <h3 className="font-bold text-[#1F2937] mb-4 flex items-center gap-2">
                  <Upload className="w-4 h-4 text-[#F59E0B]" /> Télécharger une offre (PDF/Word)
                </h3>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-slate-500 uppercase">Titre du poste</Label>
                      <Input value={uploadForm.title} onChange={e => setUploadForm(f => ({ ...f, title: e.target.value }))} required className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-500 uppercase">Lieu</Label>
                      <Input value={uploadForm.location} onChange={e => setUploadForm(f => ({ ...f, location: e.target.value }))} required className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-500 uppercase">Type de contrat</Label>
                      <Select value={uploadForm.contract_type} onValueChange={v => setUploadForm(f => ({ ...f, contract_type: v }))}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['CDI', 'CDD', 'Freelance', 'Stage', 'Alternance', 'Interim'].map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 uppercase">Fichier (PDF, DOC, DOCX — max 10 Mo)</Label>
                    <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={e => setUploadForm(f => ({ ...f, file: e.target.files?.[0] }))} className="mt-1 text-sm" />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={submitUpload} disabled={saving || !uploadForm.title || !uploadForm.location}
                      className="bg-[#F59E0B] hover:bg-[#D97706] text-white text-sm">
                      {saving ? 'Envoi...' : 'Soumettre'}
                    </Button>
                    <Button onClick={() => setUploadMode(false)} variant="ghost" className="text-sm">Annuler</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Offer form */}
            {showForm && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6" data-testid="offer-form">
                <h3 className="font-bold text-[#1F2937] mb-4 flex items-center gap-2">
                  <Edit3 className="w-4 h-4 text-[#F59E0B]" /> Nouvelle offre d'emploi
                </h3>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-slate-500 uppercase">Titre du poste *</Label>
                      <Input value={offerForm.title} onChange={e => setOfferForm(f => ({ ...f, title: e.target.value }))} className="mt-1" data-testid="offer-title" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-500 uppercase">Lieu *</Label>
                      <Input value={offerForm.location} onChange={e => setOfferForm(f => ({ ...f, location: e.target.value }))} className="mt-1" data-testid="offer-location" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-slate-500 uppercase">Type de contrat</Label>
                      <Select value={offerForm.contract_type} onValueChange={v => setOfferForm(f => ({ ...f, contract_type: v }))}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['CDI', 'CDD', 'Freelance', 'Stage', 'Alternance', 'Interim'].map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-500 uppercase">Salaire</Label>
                      <Input value={offerForm.salary} onChange={e => setOfferForm(f => ({ ...f, salary: e.target.value }))} placeholder="Ex: 300 000 F/mois" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-500 uppercase">Date de debut</Label>
                      <Input value={offerForm.start_date} onChange={e => setOfferForm(f => ({ ...f, start_date: e.target.value }))} placeholder="Ex: Septembre 2026" className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 uppercase">Description du poste *</Label>
                    <textarea value={offerForm.summary} onChange={e => setOfferForm(f => ({ ...f, summary: e.target.value }))}
                      rows={3} className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#F59E0B]" data-testid="offer-summary" />
                  </div>

                  {/* Dynamic list fields */}
                  {[
                    { key: 'missions', label: 'Missions', placeholder: 'Décrire une mission...' },
                    { key: 'profile', label: 'Profil recherché', placeholder: 'Critère...' },
                    { key: 'conditions', label: 'Conditions', placeholder: 'Condition...' },
                    { key: 'tags', label: 'Tags', placeholder: 'Ex: RH, CDI, Papeete...' },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key}>
                      <Label className="text-xs font-semibold text-slate-500 uppercase">{label}</Label>
                      {(offerForm[key] || []).map((val, i) => (
                        <div key={i} className="flex gap-2 mt-1">
                          <Input value={val} onChange={e => updateListField(setOfferForm, key, i, e.target.value)} placeholder={placeholder} />
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeListItem(setOfferForm, key, i)} className="text-red-400 hover:text-red-600">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <button type="button" onClick={() => addListItem(setOfferForm, key)} className="text-xs text-[#F59E0B] hover:underline mt-1">
                        + Ajouter
                      </button>
                    </div>
                  ))}

                  <div className="flex gap-2 pt-2">
                    <Button onClick={submitOffer} disabled={saving || !offerForm.title || !offerForm.location || !offerForm.summary}
                      className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold" data-testid="submit-offer">
                      {saving ? 'Envoi...' : 'Soumettre pour validation'}
                    </Button>
                    <Button onClick={() => setShowForm(false)} variant="ghost">Annuler</Button>
                  </div>
                  <p className="text-xs text-gray-400">L'offre sera publiée après validation par l'équipe AERH.</p>
                </div>
              </div>
            )}

            {/* Offers list */}
            {offers.length === 0 && !showForm && !uploadMode ? (
              <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
                <Briefcase className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Aucune offre publiée pour le moment.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {offers.map(offer => {
                  const st = STATUS_MAP[offer.status] || STATUS_MAP.pending;
                  const isOpen = expandedOffer === offer.id;
                  const offerApps = applications.filter(a => a.offer_id === offer.id);
                  return (
                    <div key={offer.id} className="bg-white rounded-xl border border-gray-200">
                      <div className="p-5 flex items-center justify-between cursor-pointer" onClick={() => setExpandedOffer(isOpen ? null : offer.id)}>
                        <div className="flex items-center gap-3">
                          <div>
                            <h3 className="font-bold text-sm text-[#1F2937]">{offer.title}</h3>
                            <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {offer.location}</span>
                              <span>{offer.contract_type}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {offerApps.length > 0 && (
                            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{offerApps.length} candidature(s)</span>
                          )}
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${st.color}`}>{st.label}</span>
                          {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </div>
                      </div>
                      {isOpen && (
                        <div className="border-t border-gray-100 px-5 pb-5 pt-3">
                          <p className="text-sm text-gray-600 mb-3">{offer.summary}</p>
                          <div className="flex gap-2">
                            {offer.status !== 'approved' && (
                              <Button onClick={() => deleteOffer(offer.id)} variant="ghost" size="sm" className="text-red-500 hover:text-red-700 text-xs">
                                <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: CANDIDATURES ── */}
        {tab === 'candidatures' && (
          <div data-testid="tab-content-candidatures">
            <h2 className="text-lg font-bold text-[#1F2937] mb-4">Candidatures reçues ({applications.length})</h2>
            {applications.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
                <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Aucune candidature reçue pour le moment.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {applications.map(app => (
                  <div key={app.id} className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-sm text-[#1F2937]">{app.candidate_name}</h3>
                        <p className="text-xs text-[#F59E0B] font-medium">Pour : {app.offer_title}</p>
                        {app.contact_préférence === 'direct' ? (
                          <div className="mt-2 text-xs text-gray-600 space-y-0.5">
                            {app.candidate_email && <p>Email : {app.candidate_email}</p>}
                            {app.candidate_phone && <p>Tel : {app.candidate_phone}</p>}
                          </div>
                        ) : (
                          <p className="mt-2 text-xs text-gray-400 italic">Mise en relation via AERH uniquement</p>
                        )}
                      </div>
                      <span className="text-[10px] text-gray-400">{new Date(app.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: PROFIL ── */}
        {tab === 'profil' && (
          <div className="max-w-2xl" data-testid="tab-content-profil">
            <h2 className="text-lg font-bold text-[#1F2937] mb-6">Profil entreprise</h2>
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-500 uppercase">Nom de l'entreprise</Label>
                  <Input value={profileForm.company_name} onChange={e => setProfileForm(f => ({ ...f, company_name: e.target.value }))} className="mt-1" data-testid="company-name" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500 uppercase">Secteur</Label>
                  <Input value={profileForm.sector} onChange={e => setProfileForm(f => ({ ...f, sector: e.target.value }))} className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Description</Label>
                <textarea value={profileForm.description} onChange={e => setProfileForm(f => ({ ...f, description: e.target.value }))}
                  rows={3} className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#F59E0B]" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-500 uppercase">Site web</Label>
                  <Input value={profileForm.website} onChange={e => setProfileForm(f => ({ ...f, website: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500 uppercase">Adresse</Label>
                  <Input value={profileForm.address} onChange={e => setProfileForm(f => ({ ...f, address: e.target.value }))} className="mt-1" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-500 uppercase">Nom du contact</Label>
                  <Input value={profileForm.contact_name} onChange={e => setProfileForm(f => ({ ...f, contact_name: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500 uppercase">Telephone du contact</Label>
                  <Input value={profileForm.contact_phone} onChange={e => setProfileForm(f => ({ ...f, contact_phone: e.target.value }))} className="mt-1" />
                </div>
              </div>
              <Button onClick={saveProfile} disabled={saving} className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-8 py-5" data-testid="save-profile">
                {saving ? 'Sauvegarde...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        )}

        {/* ── TAB: ABONNEMENT ── */}
        {tab === 'abonnement' && (
          <div className="max-w-2xl" data-testid="tab-content-abonnement">
            <h2 className="text-lg font-bold text-[#1F2937] mb-6">Mon abonnement</h2>
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#1F2937]">
                    {subscription?.active ? `Abonnement ${subscription.plan === 'annual' ? 'Annuel' : 'Mensuel'} actif` : 'Plan Decouverte (gratuit)'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {postingStatus?.used || 0} offre(s) publiee(s)
                    {subscription?.active
                      ? ` — ${subscription.days_remaining || 0} jour(s) restant(s)`
                      : postingStatus?.reason === 'free' ? ` — ${postingStatus?.remaining || 0} gratuite(s) restante(s)` : ''
                    }
                  </p>
                </div>
              </div>

              {subscription?.active && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-3">
                  <div className="flex items-center gap-2 text-sm text-green-700 font-medium">
                    <CheckCircle className="w-4 h-4" /> Abonnement actif — offres illimitées
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    Valide jusqu'au {new Date(subscription.end_date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}

              {!subscription?.active && (
                <div className="bg-[#F59E0B]/5 rounded-xl p-5 mt-4">
                  <p className="text-sm font-semibold text-[#1F2937] mb-3">Passez à l'illimité</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="border border-[#F59E0B] rounded-lg p-4 text-center">
                      <p className="text-xl font-bold text-[#F59E0B]">500 F<span className="text-xs text-gray-400">/mois</span></p>
                      <p className="text-xs text-gray-500 mt-1">Sans engagement</p>
                      <Button onClick={() => handleSubscribe('monthly')} disabled={subscribing}
                        size="sm" className="mt-3 bg-[#F59E0B] hover:bg-[#D97706] text-white text-xs w-full" data-testid="subscribe-monthly">
                        {subscribing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Souscrire'}
                      </Button>
                    </div>
                    <div className="border-2 border-[#0D9488] rounded-lg p-4 text-center relative">
                      <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#0D9488] text-white text-[9px] font-bold px-2 py-0.5 rounded-full">-17%</div>
                      <p className="text-xl font-bold text-[#0D9488]">5 000 F<span className="text-xs text-gray-400">/an</span></p>
                      <p className="text-xs text-gray-500 mt-1">2 mois offerts</p>
                      <Button onClick={() => handleSubscribe('annual')} disabled={subscribing}
                        size="sm" className="mt-3 bg-[#0D9488] hover:bg-[#0F766E] text-white text-xs w-full" data-testid="subscribe-annual">
                        {subscribing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Souscrire'}
                      </Button>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-3 text-center">Paiement sécurisé par Stripe. Abonnement géré par AERH.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeurDashboard;
